<template>
  <VeeForm v-slot="{ errors, handleSubmit, meta }" class="p-4 rounded-md w-96">
    <NText
      variant="caption1"
      weight="medium"
      class="text-neutral-secondary-press block pr-3 mb-2"
    >
      {{ t('common.symbol') }}
    </NText>

    <SymbolSearch
      class="w-[22rem]"
      :active-symbol-name="symbol.instrumentName"
      @update-symbol="updateSymbol"
    />

    <VeeField
      v-slot="{ field, errorMessage }"
      v-model="formModel.quantity"
      name="quantity"
    >
      <NInput
        v-mask="{
          alias: 'positiveNumber',
        }"
        :model-value="formModel.quantity"
        v-bind="field"
        class="my-2"
        type="tel"
        :error="errorMessage || errors.quantity"
        show-error
        :min="0"
        input-class="text-center mr-7"
        @focusin="$event.target.select()"
      >
        <template #prefix>
          <NText
            variant="caption1"
            class="text-neutral-secondary ml-2"
            weight="medium"
          >
            {{ t('misc.quantity') }}
          </NText>
        </template>
      </NInput>
    </VeeField>

    <VeeField
      v-slot="{ field, errorMessage }"
      v-model="formModel.price"
      name="price"
    >
      <NInput
        v-mask="{
          alias: 'positiveNumber',
        }"
        :model-value="formModel.price"
        v-bind="field"
        class="my-2"
        type="tel"
        :error="errorMessage || errors.price"
        show-error
        :min="0"
        input-class="text-center"
        @focusin="$event.target.select()"
      >
        <template #prefix>
          <NText
            variant="caption1"
            weight="medium"
            class="text-neutral-secondary ml-2 shrink-0"
          >
            {{ t('modules.portfolio.net_average') }}
          </NText>
        </template>

        <template #postfix>
          <NText variant="caption1" class="text-neutral-secondary">
            {{ t('misc.rial') }}
          </NText>
        </template>
      </NInput>
    </VeeField>

    <div class="flex mt-4">
      <NButton
        v-gid="{
          module: 'portfolio',
          screen: 'home',
          section: 'createVirtualPortfolioDialog',
          component: 'addButton',
        }"
        :disabled="isLoadingCreateVirtualPortfolioItem || !meta.valid"
        class="me-3 w-3/4"
        @click="handleSubmit($event, submit)"
      >
        <NText weight="bold" variant="caption1">
          {{ t('misc.add') }}
        </NText>
      </NButton>

      <NButton
        variant="plain"
        color="gray"
        class="w-1/4"
        @click.prevent="remove()"
      >
        <NText weight="bold" variant="caption1">
          {{ t('common.cancel') }}
        </NText>
      </NButton>
    </div>
  </VeeForm>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { reactive } from 'vue';
import { useDialog, useToast } from '@negar/core';
import { useQueryClient } from '@libs/use';
import {
  PORTFOLIO_QUERY_KEYS,
  useCreateVirtualPortfolioItemQuery,
} from '@core/portfolio';

import { SymbolSearch } from '@/modules/symbol';

import vMask from '@/directives/mask';

type FormModel = {
  price: number | null;
  quantity: number | null;
};
type SymbolType = {
  isin: string | null;
  instrumentName?: string;
};

const { t } = useI18n();

const { remove } = useDialog();
const toast = useToast();

const queryClient = useQueryClient();

const formModel = reactive<FormModel>({
  price: null,
  quantity: null,
});
const symbol = reactive<SymbolType>({
  isin: null,
  instrumentName: undefined,
});

const { createVirtualPortfolioItem, isLoadingCreateVirtualPortfolioItem } =
  useCreateVirtualPortfolioItemQuery({
    onSuccess() {
      toast.neutral({
        message: t('modules.portfolio.virtualPortfolio.addSuccessMessage', {
          name: symbol.instrumentName,
          ...formModel,
        }),
        duration: 3000,
      });

      queryClient.invalidateQueries(PORTFOLIO_QUERY_KEYS.virtualPortfolio);

      remove();
    },
    onReject() {
      toast.negative({
        message: t('modules.portfolio.virtualPortfolio.addFailedMessage', {
          name: symbol.instrumentName,
        }),
        duration: 3000,
      });
    },
  });

const submit = (formModel: FormModel) => {
  createVirtualPortfolioItem({
    averagePrice: Math.round(formModel.price as number),
    instrumentIsin: symbol.isin,
    quantity: formModel.quantity,
  });
};

const updateSymbol = ({
  isin,
  name,
}: {
  isin: string | null;
  name: string | null;
}) => {
  Object.assign(symbol, {
    isin,
    instrumentName: name,
  });
};
</script>
